import { useState } from 'react';
import { Checkbox, FormControl, FormControlLabel, MenuItem, Select, RadioGroup, Radio } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { PageCard } from '../../CardWithTitle';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { assetCommissionsStatusOptions } from '../../../../store/GeneralStore';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { SearchBox } from '../../Search/Search';
import { EntityType, SearchResult } from '../../../../store/Search';
import { CustomActionBar } from '../../DetailPanel/CustomActionBar';
import { bulkValidateLinksByAssetField } from '../../../../store/Link';
import { LinkDataByAssetRequest } from '../../../../Api/Link/LinkApi';

interface SpecificationsStepProps {
  nextStep: () => void;
  title: string;
  currentStep: number;
}

enum AssetField {
  EXPECTED_RPM_ACCOUNT = 'expectedRpmAccountNumber',
  ACCOUNT_NUMBER = 'accountNumber',
  SUPPLIER_ORDER_NUMBER = 'supplierOrderNumber'
}

interface FormData {
  setCommissionStatus: boolean;
  commissionStatus: string;
  setActualFirstRun: boolean;
  actualFirstRunType: string;
  actualFirstRun: string | null;
  salesAgentName: string;
  salesAgentId: string | null;
  supplierName: string;
  supplierId: string | null;
  assetField: string;
}

const defaultFormData: FormData = {
  setCommissionStatus: false,
  commissionStatus: 'Commissioning',
  setActualFirstRun: false,
  actualFirstRunType: 'from',
  actualFirstRun: null,
  salesAgentName: '',
  salesAgentId: null,
  supplierName: '',
  supplierId: null,
  assetField: AssetField.EXPECTED_RPM_ACCOUNT
};

const radioStyle = { height: '2rem', width: '2rem', m: '0 0.3rem' };

export const SpecificationsStep = ({ nextStep, title }: SpecificationsStepProps) => {
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const isValidStep =
    formData.salesAgentId !== null && (formData.actualFirstRunType !== 'keep' || formData.actualFirstRun !== null);

  const submitStep = async () => {
    if (!isValidStep) return;
    setIsSubmitting(true);
    const dataToSubmit: LinkDataByAssetRequest = {
      assetField: formData.assetField,
      salesAgentId: formData.salesAgentId as string
    };
    if (formData.setCommissionStatus) dataToSubmit.commissionStatus = formData.commissionStatus;
    if (formData.actualFirstRunType === 'keep' && formData.actualFirstRun)
      dataToSubmit.actualFirstRun = formData.actualFirstRun;
    if (formData.supplierId) dataToSubmit.supplierId = formData.supplierId;
    bulkValidateLinksByAssetField(dataToSubmit);
    setIsSubmitting(false);
    nextStep();
  };

  const formatDate = (dateValue: Date | null) => {
    if (!dateValue) return '';
    if (dateValue.toString() === 'Invalid Date') return '';
    return new Date(Date.UTC(dateValue.getFullYear(), dateValue.getMonth(), 1)).toISOString();
  };

  const cardActions = () => (
    <button
      className={DEFAULT_ADVANCE_BUTTON_STYLE}
      onClick={submitStep}
      data-cy="next-step-button"
      disabled={!isValidStep}
      data-testid="next-step-button">
      Next
    </button>
  );

  return (
    <PageCard
      dataTestId="specifications-step"
      dataCy="specifications-step"
      childrenWrapperClass="min-w-full overflow-visible"
      title={title}
      cardActions={cardActions()}>
      <div className="flex flex-col wrap py-6 px-10">
        <div className="flex align-center justify-center w-full">
          <div className="space-y-3 justify-center w-1/2 mr-3">
            <div className="justify-center">
              <TextBlock
                color="grey6"
                size="sm14"
                className="mb-2">
                Sales Agent
              </TextBlock>
              <div className="w-[300px]">
                <SearchBox
                  handleSelect={(result: SearchResult) => {
                    setFormData({
                      ...formData,
                      salesAgentName: result.matchedValue,
                      salesAgentId: result.id
                    });
                  }}
                  handleClearSelect={() => {
                    setFormData({ ...formData, salesAgentName: '', salesAgentId: null });
                  }}
                  searchEntityType={EntityType.AP}
                  inputString=""
                  fullWidth
                  placeholder="Search..."
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="justify-center">
              <TextBlock
                color="grey6"
                size="sm14"
                className="mb-2">
                Supplier
              </TextBlock>
              <div className="w-[300px]">
                <SearchBox
                  handleSelect={(result: SearchResult) => {
                    setFormData({
                      ...formData,
                      supplierName: result.matchedValue,
                      supplierId: result.id
                    });
                  }}
                  handleClearSelect={() => {
                    setFormData({ ...formData, supplierName: '', supplierId: null });
                  }}
                  searchEntityType={EntityType.SUPPLIER}
                  inputString=""
                  fullWidth
                  placeholder="Any"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
          <div className="space-y-3 justify-center w-1/2 mr-3">
            <TextBlock
              color="grey6"
              size="sm14">
              Field
            </TextBlock>
            <RadioGroup
              aria-label="assetField"
              name="assetField"
              value={formData.assetField}
              aria-disabled={isSubmitting}
              onChange={(e) => {
                const val = e.target.value;
                setFormData({ ...formData, assetField: val });
              }}
              className="!m-0 p-0">
              <FormControlLabel
                value={AssetField.EXPECTED_RPM_ACCOUNT}
                control={<Radio sx={radioStyle} />}
                label={
                  <TextSpan
                    color="grey6"
                    size="sm14">
                    <TextSpan
                      color="black"
                      size="sm14"
                      weight="medium"
                      className="mr-1">
                      Expected RPM Account
                    </TextSpan>
                    is RPM Account Number
                  </TextSpan>
                }
              />
              <FormControlLabel
                value={AssetField.ACCOUNT_NUMBER}
                control={<Radio sx={radioStyle} />}
                label={
                  <TextSpan
                    color="grey6"
                    size="sm14">
                    <TextSpan
                      color="black"
                      size="sm14"
                      weight="medium"
                      className="mr-1">
                      Account Number
                    </TextSpan>
                    is RPM Account Number
                  </TextSpan>
                }
              />
              <FormControlLabel
                value={AssetField.SUPPLIER_ORDER_NUMBER}
                control={<Radio sx={radioStyle} />}
                label={
                  <TextSpan
                    color="grey6"
                    size="sm14">
                    <TextSpan
                      color="black"
                      size="sm14"
                      weight="medium"
                      className="mr-1">
                      Supplier Order Number
                    </TextSpan>
                    is RPM Account Number
                  </TextSpan>
                }
              />
            </RadioGroup>
          </div>
        </div>
        <hr className="my-4 -mx-4 border-t border-grey-3" />
        <div className="justify-center w-full mb-4">
          <FormControlLabel
            disabled={isSubmitting}
            control={
              <Checkbox
                checked={formData.setCommissionStatus}
                onChange={() => setFormData({ ...formData, setCommissionStatus: !formData.setCommissionStatus })}
              />
            }
            label={
              <TextSpan
                color="grey6"
                size="sm14">
                Set Commission Status to
              </TextSpan>
            }
          />
          <FormControl
            sx={{ minHeight: '1rem', lineHeight: 1, display: 'block' }}
            fullWidth>
            <Select
              id="edit-component-select"
              size="small"
              data-testid="edit-component-select"
              value={formData.commissionStatus}
              disabled={!formData.setCommissionStatus || isSubmitting}
              onChange={(e) => {
                const val = e.target.value;
                setFormData({ ...formData, commissionStatus: val });
              }}>
              {assetCommissionsStatusOptions.map((op) => (
                <MenuItem
                  key={op}
                  value={op}>
                  {op || '--None--'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div className="space-y-3 justify-center w-1/2 mr-3">
          <TextBlock
            color="grey6"
            size="sm14">
            Set Actual First Run if empty to
          </TextBlock>
          <RadioGroup
            aria-label="afcr"
            name="afcr"
            value={formData.actualFirstRunType}
            aria-disabled={isSubmitting}
            onChange={(e) => {
              const val = e.target.value;
              const parsedVal = val === 'from' ? null : formData.actualFirstRun;
              setFormData({ ...formData, actualFirstRun: parsedVal, actualFirstRunType: val });
            }}
            className="!m-0 p-0">
            <FormControlLabel
              value={'from'}
              control={<Radio sx={radioStyle} />}
              label={
                <TextSpan
                  color="grey6"
                  size="sm14">
                  The first run of the RPM Account
                </TextSpan>
              }
            />
            <FormControlLabel
              value={'keep'}
              control={<Radio sx={radioStyle} />}
              label={
                <DatePicker
                  closeOnSelect={false}
                  disabled={isSubmitting}
                  data-testid="actual-first-run-date-picker"
                  className="w-40"
                  sx={{ minHeight: '1rem', lineHeight: 1, display: 'block' }}
                  slots={{
                    actionBar: CustomActionBar
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ['next', 'accept', 'cancel']
                    },
                    textField: {
                      size: 'small'
                    }
                  }}
                  views={['month', 'year']}
                  value={formData.actualFirstRun ? new Date(formData.actualFirstRun) : null}
                  onChange={(value) => {
                    const v = formatDate(value);
                    setFormData({ ...formData, actualFirstRun: v, actualFirstRunType: 'keep' });
                  }}
                  format={'MMM yyyy'}
                />
              }
            />
          </RadioGroup>
        </div>
      </div>
    </PageCard>
  );
};
